import { useState, useRef, useEffect } from "react";
import {
  ChevronDown,
  Trash2,
  Users,
  X,
  Check,
  Pencil,
  Tag,
  ArrowLeftRight,
  Reply,
  Shuffle,
  Receipt,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TransactionSource, TransactionType, dayjs } from "@boo/shared";
import type { AccountItem } from "@boo/shared";
import type {
  Transaction,
  ExpenseCategory,
  IncomeCategory,
  CardIssuer,
  ClassifyIncomingRequest,
} from "@boo/shared";
import { formatSource } from "../../lib/formatSource";
import { effectiveAmount as netAmount } from "../../lib/effectiveAmount";
import IncomingClassifier from "./IncomingClassifier";
import CategoryEditor from "./CategoryEditor";
import CardPaymentEditor from "./CardPaymentEditor";
import EditIncomeModal from "./EditIncomeModal";
import AccountEditor from "./AccountEditor";
import AccountAvatar from "../../../accounts/components/AccountAvatar";
import { useBillStore } from "../../../bills/store/useBillStore";
import { useAccountStore } from "../../../accounts/store/useAccountStore";
import type { UpdateTransactionPayload } from "../../../../services/expenseApi";

interface TransactionItemProps {
  transaction: Transaction;
  showDate?: boolean;
  onCategorize: (
    id: string,
    category: ExpenseCategory | IncomeCategory,
  ) => void;
  onDelete: (id: string) => void;
  onSetCardPayment?: (id: string, issuer: CardIssuer | null) => void;
  onClassifyIncoming?: (
    id: string,
    payload: ClassifyIncomingRequest,
  ) => Promise<void>;
  onUpdate?: (id: string, payload: UpdateTransactionPayload) => Promise<void>;
  onRenamePayee?: (
    rawName: string,
    alias: string,
  ) => Promise<{ matchedCount: number }>;
}

function formatBaht(n: number) {
  return `฿${n.toLocaleString("en-US")}`;
}

// Mobile: labelled buttons in the expanded action strip. From sm: the compact
// icon-only buttons that sit beside the amount.
const ACTION_BTN =
  "inline-flex items-center gap-1.5 px-3 min-h-10 sm:min-h-0 sm:p-1 rounded-lg sm:rounded-md text-[12px] font-medium bg-[var(--surface-muted)] sm:bg-transparent text-muted-foreground transition-all cursor-pointer touch-manipulation";
const ACTION_BTN_IDLE = `${ACTION_BTN} sm:opacity-40 [@media(hover:none)]:opacity-100 hover:opacity-100 hover:bg-[var(--surface-muted)]`;

export default function TransactionItem({
  transaction,
  showDate = false,
  onCategorize,
  onDelete,
  onSetCardPayment,
  onClassifyIncoming,
  onUpdate,
  onRenamePayee,
}: TransactionItemProps) {
  const [confirming, setConfirming] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [classifyOpen, setClassifyOpen] = useState(false);
  // Mobile only: actions stay hidden until the row is tapped.
  const [actionsOpen, setActionsOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renameSaving, setRenameSaving] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (renaming) renameInputRef.current?.focus();
  }, [renaming]);

  // Tapping the row toggles the action strip, unless the tap landed on a
  // control inside it or bubbled up from a portaled popover.
  function toggleActions(e: React.MouseEvent<HTMLElement>) {
    const target = e.target as HTMLElement;
    if (!e.currentTarget.contains(target)) return;
    if (target.closest("button, input")) return;
    setActionsOpen((o) => !o);
  }

  function openRename() {
    setRenameValue(transaction.displayTitle ?? transaction.title);
    setRenameError(null);
    setRenaming(true);
  }

  function cancelRename() {
    setRenaming(false);
    setRenameError(null);
  }

  async function saveRename() {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenameError("Alias cannot be empty");
      return;
    }
    if (trimmed.length < 2) {
      setRenameError("Alias must be at least 2 characters");
      return;
    }
    setRenameSaving(true);
    try {
      const { matchedCount } = await onRenamePayee!(transaction.title, trimmed);
      setRenaming(false);
      const { toast } = await import("sonner");
      toast.success(
        `Alias saved — applied to ${matchedCount} ${matchedCount === 1 ? "transaction" : "transactions"}`,
      );
    } catch (err) {
      setRenameError(
        err instanceof Error ? err.message : "Failed to save alias",
      );
    } finally {
      setRenameSaving(false);
    }
  }

  const navigate = useNavigate();
  const isSplitEligible =
    transaction.transactionType === TransactionType.EXPENSE;
  const hasBill = !!transaction.billId;

  function openBill() {
    if (transaction.billId) {
      navigate(`/bills/${transaction.billId}`);
      return;
    }
    useBillStore.getState().startNew({
      transactionId: transaction.id,
      title: transaction.title,
      date: dayjs(transaction.date).format("YYYY-MM-DD"),
    });
    navigate("/bills/new");
  }
  const isTransfer = transaction.transactionType === TransactionType.TRANSFER;
  const accounts = useAccountStore((s) => s.accounts);
  const fromAccount = accounts.find((a) => a.id === transaction.accountId);
  const toAccount = accounts.find((a) => a.id === transaction.toAccountId);
  const hasTransferRoute = !!(transaction.accountId || transaction.toAccountId);
  const isSplit = transaction.splitType != null;

  const isPayback =
    transaction.transactionType === TransactionType.REIMBURSEMENT;
  const paidBack = transaction.reimbursedAmount ?? 0;
  // Money received (other than applied recurring income) can be re-classified.
  const canReclassify =
    !!onClassifyIncoming &&
    (isPayback ||
      (transaction.transactionType === TransactionType.INCOME &&
        transaction.source !== TransactionSource.RecurringIncome));
  const showClassifier = canReclassify && classifyOpen;

  const effectiveAmount = netAmount(transaction);

  return (
    <div className="flex flex-col">
      {/* Main card */}
      <div
        className={`grid items-center gap-x-3.5 gap-y-2.5 py-3 grid-cols-[1fr_auto] sm:grid-cols-[1fr_auto_auto] sm:px-4 sm:bg-card sm:border-[0.5px] sm:border-border ${
          showClassifier ? "sm:rounded-t-[10px]" : "sm:rounded-[10px]"
        }`}
      >
        {/* Middle: name + meta */}
        <div className="flex flex-col gap-0.5 min-w-0" onClick={toggleActions}>
          {renaming ? (
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-1">
                <input
                  ref={renameInputRef}
                  value={renameValue}
                  onChange={(e) => {
                    setRenameValue(e.target.value);
                    if (renameError) setRenameError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveRename();
                    if (e.key === "Escape") cancelRename();
                  }}
                  disabled={renameSaving}
                  className="text-[13px] font-medium text-foreground bg-[var(--surface-muted)] border border-border rounded-[5px] px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-primary min-w-0 flex-1"
                />
                <button
                  onClick={saveRename}
                  disabled={renameSaving}
                  className="p-3 sm:p-0.5 rounded text-green-600 hover:bg-green-50 dark:hover:bg-green-950/30 transition-colors cursor-pointer disabled:opacity-40 touch-manipulation"
                  aria-label="Save alias"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={cancelRename}
                  disabled={renameSaving}
                  className="p-3 sm:p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-[var(--surface-muted)] transition-colors cursor-pointer disabled:opacity-40 touch-manipulation"
                  aria-label="Cancel rename"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {renameError && (
                <span className="text-[11px] text-red-500">{renameError}</span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1 min-w-0">
              <div className="text-[13px] font-medium truncate text-foreground">
                {transaction.displayTitle ?? transaction.title}
              </div>
              {onRenamePayee && (
                <button
                  onClick={openRename}
                  className="hidden sm:inline-flex shrink-0 p-0.5 rounded text-muted-foreground opacity-40 [@media(hover:none)]:opacity-100 hover:opacity-100 hover:text-foreground hover:bg-[var(--surface-muted)] transition-all cursor-pointer touch-manipulation"
                  aria-label="Rename payee"
                >
                  <Tag className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[11px] text-muted-foreground">
            <span>{formatSource(transaction.source)}</span>
            {showDate && (
              <>
                <MetaDot />
                <span>{dayjs(transaction.date).format("D MMM")}</span>
              </>
            )}
            {transaction.time && (
              <>
                <MetaDot />
                <span>{transaction.time}</span>
              </>
            )}
            {/* Chips sit on their own line on mobile, inline from sm. */}
            <MetaDot className="hidden sm:inline-block" />
            {/* Category and account stay on one line; the pair wraps together. */}
            <div className="flex min-w-0 max-w-full basis-full items-center gap-2 sm:basis-auto">
              {isTransfer ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[5px] text-[10px] font-medium bg-[var(--surface-muted)] text-muted-foreground">
                  <ArrowLeftRight className="w-3 h-3" />
                  Transfer
                  {hasTransferRoute && (
                    <>
                      <span aria-hidden="true">·</span>
                      <TransferEnd account={fromAccount} />
                      <span aria-hidden="true">→</span>
                      <TransferEnd account={toAccount} />
                    </>
                  )}
                </span>
              ) : isPayback && transaction.reimbursesTransactionId ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[5px] text-[10px] font-medium bg-[var(--surface-muted)] text-muted-foreground min-w-0">
                  <Reply className="w-3 h-3 shrink-0" />
                  <span className="truncate">
                    payback → {transaction.reimbursesTitle ?? "expense"}
                  </span>
                </span>
              ) : (
                <CategoryEditor
                  transactionId={transaction.id}
                  currentCategory={
                    transaction.transactionType === TransactionType.INCOME
                      ? (transaction.incomeCategory ?? null)
                      : transaction.category
                  }
                  transactionType={transaction.transactionType}
                  onCategorize={onCategorize}
                />
              )}
              {onSetCardPayment &&
                transaction.transactionType === TransactionType.EXPENSE && (
                  <span
                    className={
                      transaction.cardPaymentIssuer
                        ? "contents"
                        : "hidden sm:contents"
                    }
                  >
                    <CardPaymentEditor
                      transactionId={transaction.id}
                      issuer={transaction.cardPaymentIssuer ?? null}
                      isMatched={transaction.cardStatementId != null}
                      onChange={onSetCardPayment}
                    />
                  </span>
                )}
              {!isTransfer && (
                <AccountEditor
                  transactionId={transaction.id}
                  accountId={transaction.accountId}
                />
              )}
            </div>
            {isPayback && !transaction.reimbursesTransactionId && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[5px] text-[10px] font-medium bg-[var(--surface-muted)] text-muted-foreground">
                <Reply className="w-3 h-3" />
                payback
              </span>
            )}
            {paidBack > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[5px] text-[10px] font-medium bg-[var(--surface-muted)] text-muted-foreground [font-variant-numeric:tabular-nums]">
                <Reply className="w-3 h-3" />
                paid back ฿{paidBack.toLocaleString("en-US")}
              </span>
            )}
            {isSplit && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[5px] text-[10px] font-medium bg-[var(--surface-muted)] text-muted-foreground">
                <Users className="w-3 h-3" />
                split · ฿{effectiveAmount.toLocaleString("en-US")}
              </span>
            )}
          </div>
        </div>

        {/* Amount */}
        <div
          className="flex items-center gap-1 self-start sm:self-auto"
          onClick={toggleActions}
        >
          {/* Amount display */}
          {isSplit || paidBack > 0 ? (
            <div className="flex flex-col items-end">
              <span className="text-[14px] font-bold tabular-nums text-[var(--color-expense)]">
                ฿{effectiveAmount.toLocaleString("en-US")}
              </span>
              <span className="text-[10px] tabular-nums text-muted-foreground line-through">
                ฿{transaction.amount.toLocaleString("en-US")}
              </span>
            </div>
          ) : (
            <div
              className={`text-[14px] font-bold tabular-nums ${
                transaction.transactionType === TransactionType.INCOME
                  ? "text-[var(--color-income)]"
                  : isTransfer || isPayback
                    ? "text-muted-foreground"
                    : "text-[var(--color-expense)]"
              }`}
            >
              {transaction.transactionType === TransactionType.INCOME ||
              isPayback
                ? "+ "
                : ""}
              {formatBaht(transaction.amount)}
            </div>
          )}
          <button
            type="button"
            onClick={() => setActionsOpen((o) => !o)}
            aria-expanded={actionsOpen}
            aria-label={actionsOpen ? "Hide actions" : "Show actions"}
            className="sm:hidden inline-flex items-center justify-center min-w-11 min-h-11 -my-3 rounded-md text-muted-foreground cursor-pointer touch-manipulation"
          >
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${actionsOpen ? "rotate-180" : ""}`}
            />
          </button>
        </div>

        {/* Actions: expandable strip on mobile, inline icons from sm */}
        <div
          className={`col-span-2 flex-wrap items-center gap-2 sm:col-span-1 sm:flex sm:flex-nowrap sm:-ml-1.5 ${
            actionsOpen ? "flex" : "hidden"
          }`}
        >
          {onRenamePayee && (
            <button onClick={openRename} className={`sm:hidden ${ACTION_BTN}`}>
              <Tag className="w-3.5 h-3.5" />
              Rename
            </button>
          )}

          {onSetCardPayment &&
            transaction.transactionType === TransactionType.EXPENSE &&
            !transaction.cardPaymentIssuer && (
              <div className="sm:hidden">
                <CardPaymentEditor
                  transactionId={transaction.id}
                  issuer={null}
                  isMatched={false}
                  onChange={onSetCardPayment}
                  triggerLabel="Card bill"
                  triggerClassName={ACTION_BTN}
                />
              </div>
            )}

          {/* Income edit button */}
          {transaction.transactionType === TransactionType.INCOME &&
            onUpdate && (
              <>
                <button
                  onClick={() => setEditOpen(true)}
                  className={`${ACTION_BTN_IDLE} hover:text-foreground`}
                  aria-label="Edit income"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  <span className="sm:hidden">Edit</span>
                </button>
                <EditIncomeModal
                  transaction={editOpen ? transaction : null}
                  onClose={() => setEditOpen(false)}
                  onSave={async (id, payload) => {
                    await onUpdate(id, payload);
                    setEditOpen(false);
                  }}
                />
              </>
            )}

          {/* Re-classify money received */}
          {canReclassify && (
            <button
              onClick={() => setClassifyOpen((o) => !o)}
              aria-label="Re-classify incoming"
              aria-expanded={classifyOpen}
              className={`${ACTION_BTN_IDLE} hover:text-foreground`}
            >
              <Shuffle className="w-3.5 h-3.5" />
              <span className="sm:hidden">Re-classify</span>
            </button>
          )}

          {/* Itemised bill: opens the calculator, or the existing bill */}
          {isSplitEligible && (
            <button
              onClick={openBill}
              aria-label={hasBill ? "Open bill" : "Split by items"}
              className={`${ACTION_BTN_IDLE} hover:text-foreground`}
            >
              <Receipt className="w-3.5 h-3.5" />
              <span className="sm:hidden">
                {hasBill ? "Open bill" : "Split by items"}
              </span>
            </button>
          )}

          {/* Delete */}
          {confirming ? (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setConfirming(false)}
                className={`${ACTION_BTN} hover:text-foreground hover:bg-[var(--surface-muted)]`}
                aria-label="Cancel delete"
              >
                <X className="w-3.5 h-3.5" />
                <span className="sm:hidden">Cancel</span>
              </button>
              <button
                onClick={() => onDelete(transaction.id)}
                className={`${ACTION_BTN} !text-red-500 hover:!text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30`}
                aria-label="Confirm delete"
              >
                <Check className="w-3.5 h-3.5" />
                <span className="sm:hidden">Delete</span>
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              className={`${ACTION_BTN_IDLE} hover:text-red-500`}
              aria-label="Delete transaction"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span className="sm:hidden">Delete</span>
            </button>
          )}
        </div>
      </div>

      {showClassifier && (
        <div className="pb-3 sm:px-4 sm:py-3 sm:bg-card sm:border-[0.5px] sm:border-t-0 sm:border-border sm:rounded-b-[10px]">
          <IncomingClassifier
            transaction={transaction}
            onClassify={async (id, payload) => {
              await onClassifyIncoming(id, payload);
              setClassifyOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

function TransferEnd({ account }: { account: AccountItem | undefined }) {
  if (!account) return <span>?</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <AccountAvatar
        size="sm"
        type={account.type}
        bankCode={account.bankCode}
      />
      {account.name}
    </span>
  );
}

function MetaDot({ className = "" }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block h-0.5 w-0.5 rounded-full bg-neutral-400 ${className}`}
    />
  );
}
